import { flip, type OffsetOptions, offset, shift, size } from '@floating-ui/react';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import * as React from 'react';

import type { PopoverProps } from '../Popover';
import { Menu } from './Menu';
import { MenuPlaceholder } from './MenuPlaceholder';
import { MENU_VIEWPORT_PADDING } from './MenuSizing';

export {
    OverlayTrigger as MenuTrigger,
    useOverlayClose as useMenuClose,
    useOverlayTriggerRef as useMenuTriggerRef,
    useOverlayTriggerState as useMenuTriggerState,
} from '../Overlay';

/**
 * Get the middleware used for Floating UI in a menu.
 */
export function getMenuFloatingMiddleware(props: { offset: OffsetOptions }) {
    return [
        offset(props.offset),
        size({
            padding: MENU_VIEWPORT_PADDING,
            // Let menus grow to the available viewport height. A fixed height cap is applied
            // only per-palette via the `constrainHeight` opt-in (see PaletteDef), not to every
            // menu here.
            apply: ({ availableHeight, elements }) => {
                elements.floating.style.setProperty(
                    '--available-height',
                    `${Math.max(0, availableHeight)}px`
                );
            },
        }),
    ];
}

/**
 * Props used to configure a menu.
 */
export const menuPopoverProps = {
    placement: 'bottom' as const,
    floating: {
        middleware: [
            ...getMenuFloatingMiddleware({ offset: 8 }),
            flip({
                padding: 8,
                crossAxis: false,
            }),
            shift({ padding: MENU_VIEWPORT_PADDING, crossAxis: true }),
        ],
    },
    className: clsx(
        'flex max-h-[max(var(--available-height,0),15rem)] flex-col has-[[data-spine-menu][data-ready=false]]:invisible'
    ),
    autoFocus: false,
    wrap: (children) => (
        <Sentry.ErrorBoundary
            fallback={<MenuPlaceholder>Error while loading actions</MenuPlaceholder>}
        >
            <React.Suspense
                fallback={
                    <Menu>
                        <MenuPlaceholder data-spine-popover-stable-position-pending="">
                            Loading…
                        </MenuPlaceholder>
                    </Menu>
                }
            >
                {children}
            </React.Suspense>
        </Sentry.ErrorBoundary>
    ),
} satisfies Partial<PopoverProps>;
